import React from "react";
import AdminSelect from "./AdminSelect";

/**
 * Clickable column header that drives server-side sort.
 * `columnKey` must match a whitelisted backend sort_by value.
 *
 *   <SortableHeader columnKey="surname" sort={sort} onSort={setSort}>Name</SortableHeader>
 *
 * `sort` is the current `{ by, dir }` state; `onSort` replaces it.
 * Clicking the active column toggles direction; clicking a new column
 * resets to ascending.
 */
export function SortableHeader({ columnKey, sort, onSort, children, align = "left" }) {
  const active = sort?.by === columnKey;
  const next = () => {
    if (!active) return onSort({ by: columnKey, dir: "asc" });
    return onSort({ by: columnKey, dir: sort.dir === "asc" ? "desc" : "asc" });
  };
  const arrow = !active ? "↕" : sort.dir === "asc" ? "↑" : "↓";
  return (
    <th
      onClick={next}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); next(); } }}
      tabIndex={0}
      role="columnheader"
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      style={{
        cursor: "pointer", userSelect: "none",
        textAlign: align,
        whiteSpace: "nowrap",
      }}
      title={active ? `Sort ${sort.dir}` : "Click to sort"}
    >
      <span>{children}</span>
      <span style={{ marginLeft: "0.35rem", color: active ? "var(--accent)" : "var(--text-muted)", fontSize: "0.75rem", fontWeight: 600 }}>{arrow}</span>
    </th>
  );
}

/**
 * Standard pagination bar — Prev / Next buttons, page-size picker,
 * and "Showing X–Y of Z" summary.
 */
export function PaginationBar({ total, limit, offset, onLimit, onOffset }) {
  const start = total === 0 ? 0 : offset + 1;
  const end   = Math.min(offset + limit, total);
  const atFirst = offset === 0;
  const atLast  = end >= total;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap", padding: "0.75rem 0", fontSize: "0.85rem" }}>
      <span style={{ color: "var(--text-muted)" }}>Showing <strong>{start}–{end}</strong> of <strong>{total.toLocaleString()}</strong></span>
      <span style={{ flex: 1 }} />
      <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", color: "var(--text-muted)" }}>
        <span>Rows</span>
        <div className="filter-select-compact" style={{ width: 90 }}>
          <AdminSelect
            value={String(limit)}
            onChange={(v) => onLimit(Number(v))}
            options={[
              { value: "50", label: "50" },
              { value: "100", label: "100" },
              { value: "200", label: "200" },
              { value: "500", label: "500" },
            ]}
          />
        </div>
      </div>
      <button className="btn btn-outline btn-sm" onClick={() => onOffset(Math.max(0, offset - limit))} disabled={atFirst}>Prev</button>
      <button className="btn btn-outline btn-sm" onClick={() => onOffset(offset + limit)} disabled={atLast}>Next</button>
    </div>
  );
}

/** Simple controlled filter select. */
export function FilterSelect({ value, onChange, options, label, width = 160 }) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.85rem" }}>
      {label && <span style={{ color: "var(--text-muted)" }}>{label}</span>}
      <div className="filter-select-compact" style={{ width }}>
        <AdminSelect value={value} onChange={onChange} options={options} />
      </div>
    </div>
  );
}
